<script setup lang="ts">
/**
 * General product feedback modal (tutor-uox.2.a).
 *
 * Opens from the global SendFeedbackButton FAB. Captures category +
 * free-text body, plus the current window.location.href (passed
 * invisibly so triage can jump straight to the offending screen).
 *
 * On successful submit: shows a brief "thanks" state, then auto-closes
 * after 1.5 s so the student can carry on with what they were doing.
 * On error: surfaces the message, leaves the form filled in so the
 * student can retry without retyping.
 *
 * Body text is free-text PII (school names, teacher names etc.). Never
 * include it in analytics events or console.log calls — the server
 * fires the `feedback_submitted` event with `category` only.
 */
import { computed, onMounted, ref } from "vue";

import { apiClient } from "@/api/client";
import type { ProductFeedbackCategory } from "@/api/types";

const emit = defineEmits<{
  close: [];
}>();

const BODY_MAX = 2000;
const CATEGORIES: { value: ProductFeedbackCategory; label: string }[] = [
  { value: "bug", label: "Bug" },
  { value: "suggestion", label: "Suggestion" },
  { value: "praise", label: "Praise" },
  { value: "other", label: "Other" },
];

const category = ref<ProductFeedbackCategory>("bug");
const body = ref("");
const pageUrl = ref("");
const submitting = ref(false);
const error = ref<string | null>(null);
const submitted = ref(false);

const charsLeft = computed(() => BODY_MAX - body.value.length);
const bodyTooLong = computed(() => body.value.length > BODY_MAX);
const canSubmit = computed(
  () => !submitting.value && body.value.trim().length > 0 && !bodyTooLong.value,
);

onMounted(() => {
  // Capture the page the user was on when they opened the modal.
  // Done at mount time (not at submit) so the URL reflects the
  // surface that triggered the feedback, not the modal's own state.
  pageUrl.value = window.location.href;
});

async function submit(): Promise<void> {
  if (!canSubmit.value) return;
  error.value = null;
  submitting.value = true;
  try {
    await apiClient.submitGeneralFeedback({
      category: category.value,
      body: body.value.trim(),
      page_url: pageUrl.value || null,
    });
    submitted.value = true;
    setTimeout(() => emit("close"), 1500);
  } catch (e) {
    error.value =
      e instanceof Error
        ? e.message
        : "Could not send feedback. Please try again.";
  } finally {
    submitting.value = false;
  }
}
</script>

<template>
  <div
    class="modal-backdrop"
    role="dialog"
    aria-modal="true"
    aria-labelledby="send-feedback-title"
    data-testid="send-feedback-modal"
  >
    <div class="modal">
      <h2 id="send-feedback-title">Send us feedback</h2>

      <template v-if="!submitted">
        <p class="muted">
          Found a bug, got a suggestion, or just want to tell us how it's going?
          We read every submission.
        </p>

        <fieldset class="categories">
          <legend class="visually-hidden">Category</legend>
          <label
            v-for="opt in CATEGORIES"
            :key="opt.value"
            :class="['category-chip', { active: category === opt.value }]"
          >
            <input
              v-model="category"
              type="radio"
              name="feedback-category"
              :value="opt.value"
              :data-testid="`feedback-category-${opt.value}`"
            />
            <span>{{ opt.label }}</span>
          </label>
        </fieldset>

        <label for="feedback-body" class="visually-hidden">Feedback</label>
        <textarea
          id="feedback-body"
          v-model="body"
          rows="5"
          :maxlength="BODY_MAX"
          placeholder="What's on your mind?"
          data-testid="feedback-body"
        />
        <div class="counter-row">
          <span :class="['counter', { over: bodyTooLong }]">{{
            charsLeft
          }}</span>
        </div>

        <p v-if="error" class="error" role="alert" data-testid="feedback-error">
          {{ error }}
        </p>

        <div class="modal-actions">
          <button
            class="secondary"
            type="button"
            data-testid="feedback-cancel"
            @click="emit('close')"
          >
            Cancel
          </button>
          <button
            class="primary"
            type="button"
            :disabled="!canSubmit"
            data-testid="feedback-submit"
            @click="submit"
          >
            {{ submitting ? "Sending…" : "Send feedback" }}
          </button>
        </div>
      </template>

      <template v-else>
        <p class="thanks" data-testid="feedback-thanks">
          Thanks — we'll read every one.
        </p>
      </template>
    </div>
  </div>
</template>

<style scoped>
.categories {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  border: 0;
  padding: 0;
  margin: 0 0 0.75rem 0;
}

.category-chip {
  display: inline-flex;
  align-items: center;
  padding: 0.375rem 0.75rem;
  border: 1px solid var(--border-subtle, #d8d2c2);
  border-radius: 999px;
  font-size: 0.875rem;
  color: var(--text-muted, #6b6b6b);
  cursor: pointer;
  transition:
    background 0.15s,
    color 0.15s,
    border-color 0.15s;
}

.category-chip input {
  /* Functional but hidden — the chip is the entire hit target. */
  position: absolute;
  opacity: 0;
  pointer-events: none;
}

.category-chip.active {
  background: var(--accent, #2b2b2b);
  color: var(--surface, #faf6ec);
  border-color: var(--accent, #2b2b2b);
}

.counter-row {
  display: flex;
  justify-content: flex-end;
  margin-top: 0.25rem;
}

.counter {
  font-size: 0.75rem;
  color: var(--text-muted, #6b6b6b);
}

.counter.over {
  color: var(--danger, #b00020);
}

.error {
  font-size: 0.875rem;
  color: var(--danger, #b00020);
  margin: 0.5rem 0 0 0;
}

.thanks {
  font-size: 1rem;
  margin: 0.5rem 0 0 0;
}

.visually-hidden {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  border: 0;
}
</style>
